import React from 'react';
import { connect } from 'react-redux';

import Page404 from '../components/page-404/page-404';
import { NAVIGATION } from '../config';
import { CreateOrganizationContainer } from '../containers/create-organization/create-organization.lazy';
import { LiveStreams } from '../containers/live-streams/live-streams.data-loader';
import { LoginContainer } from '../containers/login/login.lazy';
import { SelectOrganizationContainer } from '../containers/select-organization/select-organization.lazy';
import { Applications } from '../features/applications/applications-preview-list/applications.data-loader';
import { ApplicationOverview } from '../features/applications/application-overview/application-overview.data-loader';
import { ExternalUser } from '../features/external-user/external-user';
import { FileManager } from '../features/file-manager/file-manager.data-loader';
import { FLowsLazyContainer } from '../features/flows/flows.lazy';
import { ProjectOverviewDataLoader } from '../features/project-overview/project-overview.data-loader';
import { Projects } from '../features/projects/projects.data-loader';
import { ReplicationsDataLoader } from '../features/replications/replications.data-loader';
import { TeamDataLoader } from '../features/team/team.data-loader';
import { ExternalUserLayout } from '../layouts/external-user-layout/external-user-layout';
import OnBoardingLayout from '../layouts/onboarding/onboarding';
import RootLayout from '../layouts/root/root';
import UserLayout from '../layouts/user/user';
import {
  hasInvitations,
  hasOwnOrganizations,
} from '../selectors/organizations/organizations';
import { isLoggedIn as isSessionLoggedIn } from '../selectors/session/session';
import { RouterContext } from '../types/router';
import { redirect } from '../utils/navigation';

import {
  forLoggedUsersWithOrganization,
  forLoggedUsersWithoutOrganization,
  onlyForGuests,
  onlyForLoggedUsers,
} from './permissions/permissions';
import { RedirectRoute } from './redirect-route';

import { Route } from './route';
import { RouterBootstrapContainer } from './router-bootstrap';
import { Jobs } from '../features/jobs/jobs.data-loader';

interface Props {
  hasInvitation: boolean;
  hasOrganization: boolean;
  isLoggedIn: boolean;
  beforeOpenJobCreatePage(): Promise<any>;
  beforeOpenJobsListPage(): Promise<any>;
}

const Router = (props: Props): any => {
  const { isLoggedIn, hasInvitation, hasOrganization } = props;

  const hasInvitationOrOrganization = hasInvitation || hasOrganization;

  const allowForLoggedUsers = onlyForLoggedUsers(isLoggedIn);
  const allowForGuests = onlyForGuests(isLoggedIn);

  const allowForUsersWithOrganization = forLoggedUsersWithOrganization(
    isLoggedIn,
    hasInvitationOrOrganization,
  );
  const allowForUsersWithoutOrganization = forLoggedUsersWithoutOrganization(
    isLoggedIn,
    hasInvitationOrOrganization,
  );

  return (
    <RouterBootstrapContainer>
      {(): any => (
        <RootLayout>
          <Route
            path={NAVIGATION.HOME_STATE}
            beforeEnter={() => redirect(NAVIGATION.LOGIN_STATE)}
          />

          <Route
            path={NAVIGATION.LOGIN_STATE}
            layout={OnBoardingLayout}
            beforeEnter={(next) => allowForGuests(next)}
          >
            <LoginContainer />
          </Route>

          <Route
            path={NAVIGATION.APPLICATION_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <Applications />
          </Route>

          <Route
            path={NAVIGATION.TEAM_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <TeamDataLoader />
          </Route>

          <Route
            path={NAVIGATION.APPLICATION_OVERVIEW_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <ApplicationOverview />
          </Route>

          <Route
            path={NAVIGATION.LIVE_STREAMS}
            layout={UserLayout}
            beforeEnter={(next) => allowForLoggedUsers(next)}
          >
            <LiveStreams />
          </Route>

          <Route
            path={NAVIGATION.FLOWS_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <FLowsLazyContainer />
          </Route>

          <RedirectRoute
            path={NAVIGATION.MEDIA_ASSETS}
            redirectToPath={NAVIGATION.FILE_MANAGER}
          />

          <Route
            path={NAVIGATION.FILE_MANAGER}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <FileManager />
          </Route>

          <Route
            path={NAVIGATION.ORGANIZATION_CREATE_STATE}
            layout={OnBoardingLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <CreateOrganizationContainer />
          </Route>

          <Route
            path={NAVIGATION.ORGANIZATION_CREATE_FIRST_STATE}
            layout={OnBoardingLayout}
            beforeEnter={(next) => allowForUsersWithoutOrganization(next)}
          >
            <CreateOrganizationContainer />
          </Route>

          <Route
            path={NAVIGATION.ORGANIZATION_SELECT_STATE}
            layout={OnBoardingLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <SelectOrganizationContainer />
          </Route>

          <Route
            path={NAVIGATION.PROJECTS_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForLoggedUsers(next)}
          >
            {(routerContext: RouterContext) => (
              <Projects router={routerContext} />
            )}
          </Route>

          <Route
            path={NAVIGATION.PROJECT_OVERVIEW}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <ProjectOverviewDataLoader />
          </Route>

          <Route path={NAVIGATION.REPLICATIONS_STATE} layout={UserLayout}>
            <ReplicationsDataLoader />
          </Route>

          <Route
            path={NAVIGATION.JOBS_STATE}
            layout={UserLayout}
            beforeEnter={(next) => allowForUsersWithOrganization(next)}
          >
            <Jobs />
          </Route>

          <Route path={NAVIGATION.EXTERNAL_USER} layout={ExternalUserLayout}>
            <ExternalUser />
          </Route>

          <Route path="/(.*)" layout={OnBoardingLayout}>
            <Page404 />
          </Route>
        </RootLayout>
      )}
    </RouterBootstrapContainer>
  );
};

interface StateFromProps {
  isLoggedIn: boolean;
  hasInvitation: boolean;
  hasOrganization: boolean;
}

const mapStateToProps = (state) => ({
  isLoggedIn: isSessionLoggedIn(state),
  hasInvitation: hasInvitations(state),
  hasOrganization: hasOwnOrganizations(state),
});

export default connect<StateFromProps, any, any>(mapStateToProps)(Router);
